import type { DragEventHandler, MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useHeadSlot, useLeaveWrite } from '../shell/headSlot';
import './table.css';

export interface BarWord {
  id: string;
  label: string;
  /** Its panel is open. */
  on: boolean;
  onPress: (event: ReactMouseEvent<HTMLButtonElement>) => void;
}

/**
 * The page's words, in the shell's head: the words for what the page holds after the places, and
 * Note at the far end, left of Search. [quiet] is a line of state left of Note. Drawn in place where
 * there is no head, as a page on its own is.
 */
export function PageBar({
  words,
  quiet,
  note,
}: {
  words: BarWord[];
  quiet?: string;
  note: {
    onPress?: () => void;
    /** Why Note does nothing, where it does not. */
    title?: string;
    pressed?: boolean;
    hot?: boolean;
    buttonRef?: RefObject<HTMLButtonElement | null>;
    onDragOver?: DragEventHandler<HTMLButtonElement>;
    onDragLeave?: DragEventHandler<HTMLButtonElement>;
    onDrop?: DragEventHandler<HTMLButtonElement>;
  };
}) {
  const slot = useHeadSlot();
  useLeaveWrite();
  const bar = (
    <nav className="table-bar" aria-label="This page">
      {words.length > 0 && <span className="table-bar-rule" aria-hidden="true" />}
      <span className="table-bar-words">
        {words.map((word) => (
          <button key={word.id} type="button" className="table-word" aria-pressed={word.on} onClick={word.onPress}>
            {word.label}
          </button>
        ))}
      </span>
      <span className="table-bar-spacer" />
      {quiet && <span className="table-bar-quiet">{quiet}</span>}
      <button
        ref={note.buttonRef}
        type="button"
        className={`table-word table-bar-note nook-write${note.hot ? ' hot' : ''}`}
        aria-pressed={note.pressed}
        disabled={!note.onPress}
        title={note.title}
        onClick={note.onPress}
        onDragOver={note.onDragOver}
        onDragLeave={note.onDragLeave}
        onDrop={note.onDrop}
      >
        <PlusGlyph /> Note
      </button>
    </nav>
  );
  return slot ? createPortal(bar, slot) : bar;
}

function PlusGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
